import { useEffect, useState } from "react";
import { Button } from "@radix-ui/themes";
import { Link, useLocation } from "react-router-dom";
import { useDailyLogin } from "../hooks/useDailyLogin";
import { sfxWin } from "../lib/soundEffects";
import { hasTakenFirstActions, PLAYER_ACTION_EVENT } from "../lib/playerEngagement";
import { DailyLoginLadder } from "./DailyLoginLadder";
import "./WelcomeActivationModal.css";
import "./DailyLoginPopup.css";
import { useI18n } from '../i18n/i18nContext';

/**
 * Where the game opens (home and the roster). The popup waits for one of these so it never
 * stacks on a pet's own greeting bubble or interrupts a battle.
 */
const DAILY_LOGIN_HUB_PATHS = new Set(["/", "/play"]);

interface DailyLoginPopupProps {
  /** Wait while a higher-priority popup (welcome, season) is on screen. */
  suppressed: boolean;
}

/**
 * The daily login reward. Once a day, when a signed-in trainer opens the game, this shows the
 * 10-day ladder with today highlighted; one tap on Claim pays Trainer Suika and closes it. The
 * last day is the big one, so a player opens the game to keep the streak — and then plays.
 * Rules live in shared/domain/dailyLoginRewards.ts; the claim is one atomic backend call.
 *
 * It is a one-shot offer, so closing it is not forfeiting: the same streak stays claimable on
 * /daily-rewards.
 */
export function DailyLoginPopup({ suppressed }: DailyLoginPopupProps) {
  const { t, rich } = useI18n();
  const { isConnected, status, claim, claiming, paid, error } = useDailyLogin();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  // A brand-new trainer meets their Suikamon first: the ladder waits for a few real actions.
  // A returning trainer has those already, so for them it still opens with the game.
  const [hasPlayed, setHasPlayed] = useState(hasTakenFirstActions);

  useEffect(() => {
    if (hasPlayed) return;
    const recheck = () => setHasPlayed(hasTakenFirstActions());
    window.addEventListener(PLAYER_ACTION_EVENT, recheck);
    return () => window.removeEventListener(PLAYER_ACTION_EVENT, recheck);
  }, [hasPlayed]);

  if (!isConnected || suppressed || dismissed || !hasPlayed || !status?.success || !status.rewards) return null;
  if (!DAILY_LOGIN_HUB_PATHS.has(pathname) && paid === null) return null;
  if (!status.canClaim && paid === null) return null;

  const rewards = status.rewards;
  const today = status.day ?? 1;
  const lastDay = rewards.length;

  const handleClaim = async () => {
    if (await claim()) sfxWin();
  };

  return (
    <div className="activation-overlay" role="presentation" onClick={() => setDismissed(true)}>
      <div
        className="activation-card daily-login"
        role="dialog"
        aria-modal="true"
        aria-labelledby="daily-login-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="activation-close" onClick={() => setDismissed(true)} aria-label={t('common.action.close')}>
          ✕
        </button>
        <div className="daily-login__body">
          <p className="activation-eyebrow">{t('progression.daily.popupEyebrow')}</p>
          <h2 id="daily-login-title" className="activation-title activation-title--sm">
            {t('progression.daily.popupDay', { day: today, lastDay })}
          </h2>
          <DailyLoginLadder rewards={rewards} today={today} claimed={paid !== null} />
          {paid === null ? (
            <>
              <p className="activation-subtitle daily-login__hint">
                {rich('progression.daily.popupHint', {
                  lastDay,
                  amount: rewards[lastDay - 1],
                  link: (chunks) => (
                    <Link to="/daily-rewards" onClick={() => setDismissed(true)}>
                      {chunks}
                    </Link>
                  ),
                })}
              </p>
              <Button className="suikamon-btn-view daily-login__claim" onClick={handleClaim} disabled={claiming}>
                {claiming ? t('progression.daily.claiming') : t('progression.daily.popupClaim', { amount: rewards[today - 1] })}
              </Button>
            </>
          ) : (
            <>
              <p className="activation-subtitle daily-login__hint">
                {t('progression.daily.popupPaid', { amount: paid })}
              </p>
              <Button className="suikamon-btn-view daily-login__claim" onClick={() => setDismissed(true)}>
                {t('progression.daily.popupPlay')}
              </Button>
            </>
          )}
          {error && <p className="daily-login__error" role="alert">{error}</p>}
        </div>
      </div>
    </div>
  );
}
